import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FixedSizeList as List } from 'react-window';
import './SearchBar.scss';
import { executeFetch } from '../../../helper/Api';
import { Product } from '../../../types/product';
import { useNavigate } from 'react-router-dom';

const SearchBarMobile = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // API call function
  const fetchResults = async (search: string) => {
    if (search.length < 3) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await executeFetch<Product[]>(`Products/SearchProducts/${search}`);
      if (!response.error) {
        setResults(response.data || []);
        setShowResults(true);
      }
    } catch (err) {
      setError('Error fetching results');
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input changes
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchResults(searchQuery);
  };

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (resultsRef.current && !resultsRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const product = results[index];
    return (
      <div 
        style={style}
        className="search-item"
        onClick={() => {
          setShowResults(false);
          setIsOpen(false);
          navigate(`/product/${product?.sku}`);
        }}
      >
        <div className="product">
          <div className='product-banner'>
            <img src={product.banner}  />
          </div>
          <div className="product-details">
            <div className="product-title">{product.name}</div>
            <div className="product-sku">SKU: {product.sku}</div>
          </div>
          <div className="product-price">{product.b2cPrice} €</div>
        </div>
      </div>
    );
  };

  return (
    <div className="search-bar-mobile" ref={resultsRef}>
      <FontAwesomeIcon 
        icon={faSearch} 
        className="search-icon" 
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setSearchQuery('');
            setResults([]);
            setShowResults(false);
          }
        }}
      />
      
      {isOpen && (
        <div className='search-wrap'>
          <form className="search-bar" onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearch}
              onClick={() => results.length > 0 && setShowResults(true)}
              className="search-input"
              autoFocus
            />
            <button type="submit" className="search-button" disabled={isLoading}>
              {isLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin className="search-icon" />
              ) : (
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
              )}
            </button>
          </form>

          {showResults && (
            <div className="search-results-mobile">
              {isLoading ? (
                <div className="search-status">
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                </div>
              ) : error ? (
                <div className="search-status error">{error}</div>
              ) : results.length === 0 ? (
                <div className="search-status">No results found</div>
              ) : (
                <List
                  height={Math.min(300, results.length * 80)} 
                  itemCount={results.length}
                  itemSize={80}
                  width="100%"
                  className="virtualized-list"
                >
                  {Row}
                </List>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBarMobile;